import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Play, Trophy, HelpCircle, Zap, Gamepad2, Share2, ShoppingBag, LogIn, LogOut, User, Radio, Gift, Ban, ShieldCheck, Globe } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { LanguageSelector } from "../components/LanguageSelector";
import { SoundToggle } from "../components/SoundToggle";
import { useAuth } from "../auth/AuthContext";
import { sfx, resumeAudio } from "../lib/sound";
import { api } from "../lib/api";

export const HomeScreen = ({ pseudo, setPseudo, onPlay, onLeaderboard, onHowTo, onStore, onLegal, onLive, onKit, onBlog, challenge }) => {
  const { t } = useLang();
  const { user, login, logout } = useAuth();
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  // Sur les portails (CrazyGames…) le jeu tourne en iframe → on masque la boutique
  // à paiement externe (Stripe), interdite par les portails.
  const EMBEDDED = typeof window !== "undefined" && window.self !== window.top;

  useEffect(() => {
    api.getLiveStats().then(setStats).catch(() => {});
  }, []);

  const handlePlay = () => {
    resumeAudio();
    if (!pseudo || pseudo.trim().length < 2 || pseudo.trim().length > 20) {
      setError(t("pseudoRequired"));
      sfx.fail();
      return;
    }
    setError("");
    sfx.click();
    onPlay();
  };

  return (
    <div className="relative flex min-h-full flex-col px-6 py-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-widest text-white/50">
          <Zap size={16} className="text-[#f093fb]" /> Destin Express
        </div>
        <div className="flex items-center gap-2">
          {user ? (
            <button
              onClick={logout}
              data-testid="logout-button"
              className="flex h-10 items-center gap-2 rounded-full border border-white/15 bg-white/5 px-2 pr-3 text-white/70 transition-transform hover:scale-105"
            >
              {user.picture ? (
                <img src={user.picture} alt="" className="h-6 w-6 rounded-full" />
              ) : (
                <User size={14} />
              )}
              <LogOut size={14} />
            </button>
          ) : (
            <button
              onClick={login}
              data-testid="login-button"
              className="flex h-10 items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 text-xs font-semibold text-white/70 transition-transform hover:scale-105"
            >
              <LogIn size={14} className="text-[#f093fb]" /> Google
            </button>
          )}
          <SoundToggle />
          <LanguageSelector />
        </div>
      </div>

      {challenge && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          data-testid="challenge-banner"
          className="mt-4 rounded-2xl border border-[#f093fb]/40 bg-gradient-to-r from-[#f093fb]/15 to-[#f5576c]/15 px-4 py-3 text-center text-sm font-semibold text-white"
        >
          {t("challengeBanner", {
            pseudo: challenge.pseudo,
            score: (challenge.score || 0).toLocaleString(),
          })}
        </motion.div>
      )}

      {stats && (stats.activeLives > 0 || stats.challengesToday > 0) && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs font-semibold text-white/60" data-testid="live-stats-bar">
          {stats.activeLives > 0 && (
            <span className="flex items-center gap-1.5" data-testid="stat-lives">
              <span className="h-2 w-2 animate-pulse rounded-full bg-[#f5576c]" />
              {t("statsLives", { n: stats.activeLives })}
            </span>
          )}
          {stats.challengesToday > 0 && (
            <span className="flex items-center gap-1.5 text-[#38ef7d]" data-testid="stat-challenges">
              🎯 {t("statsChallenges", { n: stats.challengesToday })}
            </span>
          )}
        </div>
      )}

      <div className="flex flex-1 flex-col justify-center py-10">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="font-display text-5xl font-black uppercase leading-[0.95] tracking-tighter sm:text-6xl"
        >
          <span className="text-white">Destin</span>
          <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#f093fb] to-[#f5576c]">
            Express
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-6 max-w-sm text-lg font-medium text-white/75"
        >
          {t("tagline")}
        </motion.p>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-2 max-w-sm text-sm text-white/45"
        >
          {t("subtagline")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-10 space-y-4"
        >
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-widest text-white/40">
              {t("pseudoLabel")}
            </label>
            <input
              data-testid="pseudo-input"
              value={pseudo}
              onChange={(e) => setPseudo(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handlePlay()}
              placeholder={t("pseudoPlaceholder")}
              maxLength={20}
              className="w-full rounded-2xl border border-white/15 bg-white/5 px-5 py-4 font-display text-lg font-bold text-white placeholder:font-body placeholder:font-normal placeholder:text-white/30 outline-none transition-colors focus:border-[#f093fb]"
            />
            {error && (
              <p data-testid="pseudo-error" className="mt-2 text-sm text-[#f5576c]">
                {error}
              </p>
            )}
          </div>

          <motion.button
            whileTap={{ scale: 0.96 }}
            onClick={handlePlay}
            data-testid="play-button"
            className="flex w-full items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-[#f093fb] to-[#f5576c] py-5 font-display text-xl font-extrabold uppercase tracking-wide text-white shadow-[0_10px_40px_-8px_rgba(240,147,251,0.6)] transition-transform"
          >
            <Play size={22} fill="white" /> {t("play")}
          </motion.button>

          <div className="grid grid-cols-2 gap-3">
            <motion.button
              whileTap={{ scale: 0.96 }}
              onClick={onLeaderboard}
              data-testid="leaderboard-button"
              className="flex items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/5 py-4 text-sm font-semibold text-white/85 transition-colors hover:bg-white/10"
            >
              <Trophy size={18} className="text-[#f093fb]" /> {t("leaderboard")}
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.96 }}
              onClick={onHowTo}
              data-testid="howto-button"
              className="flex items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/5 py-4 text-sm font-semibold text-white/85 transition-colors hover:bg-white/10"
            >
              <HelpCircle size={18} className="text-[#f093fb]" /> {t("howToPlay")}
            </motion.button>
          </div>

          {!EMBEDDED && (
            <motion.button
              whileTap={{ scale: 0.96 }}
              onClick={onStore}
              data-testid="store-button"
              className="flex w-full items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/5 py-4 text-sm font-semibold text-white/85 transition-colors hover:bg-white/10"
            >
              <ShoppingBag size={18} className="text-[#f093fb]" /> {t("store")}
            </motion.button>
          )}

          <motion.button
            whileTap={{ scale: 0.96 }}
            onClick={onLive}
            data-testid="live-button"
            className="relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-2xl border border-[#f5576c]/40 bg-[#f5576c]/10 py-4 text-sm font-bold text-white transition-colors hover:bg-[#f5576c]/20"
          >
            <span className="absolute left-4 h-2 w-2 animate-pulse rounded-full bg-[#f5576c]" />
            <Radio size={18} className="text-[#f5576c]" /> {t("live")}
          </motion.button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-6 flex flex-wrap justify-center gap-2"
          data-testid="trust-badges"
        >
          {[
            { icon: Gift, label: t("badgeFree") },
            { icon: Ban, label: t("badgeNoSignup") },
            { icon: ShieldCheck, label: t("badgePrivacy") },
            { icon: Globe, label: t("badgeLangs") },
          ].map((b, i) => (
            <span
              key={i}
              data-testid={`trust-badge-${i}`}
              className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[11px] font-semibold text-white/60"
            >
              <b.icon size={13} className="text-[#38ef7d]" /> {b.label}
            </span>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.45 }}
          className="mt-8 flex items-center justify-between gap-2"
        >
          {[
            { icon: Gamepad2, label: t("stepPick") },
            { icon: Zap, label: t("stepDecide") },
            { icon: Share2, label: t("stepShare") },
          ].map((s, i) => (
            <React.Fragment key={i}>
              <div className="flex flex-1 flex-col items-center gap-2 rounded-2xl border border-white/8 bg-white/[0.03] py-3">
                <s.icon size={18} className="text-[#f093fb]" />
                <span className="font-display text-[11px] font-bold uppercase tracking-widest text-white/60">
                  {s.label}
                </span>
              </div>
              {i < 2 && <span className="text-white/20">→</span>}
            </React.Fragment>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-6 rounded-2xl border border-white/8 bg-white/[0.03] p-5"
          data-testid="about-block"
        >
          <h2 className="font-display text-sm font-bold uppercase tracking-widest text-white/70">
            {t("aboutTitle")}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-white/55">{t("aboutBody")}</p>
        </motion.div>
      </div>

      <footer className="pt-6 text-center">
        <p className="font-display text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#f093fb] to-[#f5576c]">
          #DestinExpress
        </p>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] font-medium text-white/40">
          <button
            data-testid="footer-privacy-link"
            onClick={() => onLegal("privacy")}
            className="transition-colors hover:text-white/70"
          >
            {t("footerLegal")}
          </button>
          <span className="text-white/15">·</span>
          <button
            data-testid="footer-notice-link"
            onClick={() => onLegal("notice")}
            className="transition-colors hover:text-white/70"
          >
            {t("footerNotice")}
          </button>
          <span className="text-white/15">·</span>
          <button
            data-testid="footer-terms-link"
            onClick={() => onLegal("terms")}
            className="transition-colors hover:text-white/70"
          >
            {t("footerTerms")}
          </button>
          <span className="text-white/15">·</span>
          <button
            data-testid="footer-blog-link"
            onClick={onBlog}
            className="transition-colors hover:text-white/70"
          >
            {t("blog")}
          </button>
          <span className="text-white/15">·</span>
          <button
            data-testid="footer-contact-link"
            onClick={() => onLegal("notice")}
            className="transition-colors hover:text-white/70"
          >
            {t("contact")}
          </button>
          {onKit && (
            <>
              <span className="text-white/15">·</span>
              <button
                data-testid="footer-kit-link"
                onClick={onKit}
                className="transition-colors hover:text-[#f093fb]"
                title="Kit de lancement (créateur)"
              >
                Kit
              </button>
            </>
          )}
        </div>
        <p className="mt-4 text-[11px] leading-relaxed text-white/35" data-testid="footer-editor">
          {t("publishedBy")} destinexpress.com · France · contact@destinexpress.com
        </p>
        <p className="mt-1 text-[11px] text-white/25">© 2026 destinexpress.com</p>
      </footer>
    </div>
  );
};
